import { useState, useMemo, useEffect, useCallback, useRef, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { trpc } from "../hooks/trpc";
import { Users, Building2, FolderKanban, Search, UserCheck, UsersRound, ArrowUpDown, Check, X } from "lucide-react";
import { Eyebrow } from "../components/brand";
import { useAuth } from "../hooks/useAuth";
import { isGfRole } from "../../../shared/permissions";
import { INSTANCE } from "../lib/instance";

function fmtDate(v: any): string {
  if (!v) return "—";
  if (v instanceof Date) return v.toLocaleDateString("de");
  if (typeof v === "string") return new Date(v).toLocaleDateString("de");
  return String(v);
}

type Tab = "mitarbeiter" | "externe" | "personen" | "kunden" | "projekte";

const tabs: { key: Tab; label: string; icon: typeof Users }[] = [
  { key: "mitarbeiter", label: "Mitarbeiter", icon: Users },
  { key: "externe", label: "Externe", icon: UserCheck },
  { key: "personen", label: "Alle Personen", icon: UsersRound },
  { key: "kunden", label: "Kunden", icon: Building2 },
  { key: "projekte", label: "Projekte", icon: FolderKanban },
];

// Hook: Inline-Edit-Zustand mit Dirty-Check, Keyboard-Handling und Mobile-Modal-Fallback.
function useRowEdit<TData extends { id?: string; uuid?: string }>(opts: {
  getSnapshot: (row: TData) => Record<string, any>;
  onSave: (id: string, data: Record<string, any>) => Promise<void> | void;
}) {
  const [editId, setEditId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Record<string, any>>({});
  const initialDataRef = useRef<Record<string, any>>({});

  const isDirty = useMemo(() => {
    const init = initialDataRef.current;
    for (const k of Object.keys(editData)) {
      if ((editData[k] ?? "") !== (init[k] ?? "")) return true;
    }
    return false;
  }, [editData]);

  const rowId = (row: TData): string => String(row.id ?? row.uuid ?? "");

  const tryStartEdit = useCallback((row: TData) => {
    const id = rowId(row);
    if (!id) return;
    if (editId && editId !== id) {
      const init = initialDataRef.current;
      let dirty = false;
      for (const k of Object.keys(editData)) {
        if ((editData[k] ?? "") !== (init[k] ?? "")) { dirty = true; break; }
      }
      if (dirty && !window.confirm("Ungespeicherte Aenderungen verwerfen?")) return;
    }
    const snap = opts.getSnapshot(row);
    initialDataRef.current = { ...snap };
    setEditData(snap);
    setEditId(id);
  }, [editId, editData, opts]);

  const cancel = useCallback(() => {
    setEditId(null);
    setEditData({});
    initialDataRef.current = {};
  }, []);

  const save = useCallback(async () => {
    if (!editId) return;
    try {
      await opts.onSave(editId, editData);
      cancel();
    } catch {
      // Toast/Rollback durch Mutation-Handler; wir lassen den Edit-Mode offen.
    }
  }, [editId, editData, opts, cancel]);

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "Escape") { e.preventDefault(); cancel(); }
    else if (e.key === "Enter" && (e.target as HTMLElement).tagName !== "TEXTAREA") {
      e.preventDefault();
      save();
    }
  }, [cancel, save]);

  return { editId, editData, setEditData, isDirty, tryStartEdit, cancel, save, onKeyDown };
}

function useIsMobile(breakpoint = 768): boolean {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

// Stop-Propagation-Wrapper fuer Edit-Controls (verhindert Zeilen-Click).
function stop(e: MouseEvent<HTMLElement>) { e.stopPropagation(); }

function EditActions({ onSave, onCancel, saving }: { onSave: () => void; onCancel: () => void; saving?: boolean }) {
  return (
    <div className="flex gap-1" onClick={stop}>
      <button
        onClick={onSave}
        disabled={saving}
        className="p-1.5 cursor-pointer"
        style={{ color: "var(--color-success)", borderRadius: "var(--radius-sm)" }}
        title="Speichern (Enter)"
      >
        <Check size={14} strokeWidth={1.5} />
      </button>
      <button
        onClick={onCancel}
        className="p-1.5 cursor-pointer"
        style={{ color: "var(--color-danger)", borderRadius: "var(--radius-sm)" }}
        title="Abbrechen (Esc)"
      >
        <X size={14} strokeWidth={1.5} />
      </button>
    </div>
  );
}

// Mobile-Fallback: dasselbe Edit-UI in einem Modal.
function MobileEditModal({ open, title, onSave, onCancel, onKeyDown, children, saving }: {
  open: boolean; title: string; onSave: () => void; onCancel: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void; children: ReactNode; saving?: boolean;
}) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 50,
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
      onClick={onCancel}
      onKeyDown={onKeyDown}
    >
      <div
        onClick={stop}
        style={{
          background: "var(--color-card)", width: "100%", maxWidth: 520,
          borderRadius: "12px 12px 0 0", padding: 20,
          borderTop: "1px solid var(--color-border)",
        }}
      >
        <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: "var(--font-display)", color: "var(--color-foreground)" }}>{title}</h3>
        <div className="flex flex-col gap-3 mb-4">{children}</div>
        <div className="flex justify-end">
          <EditActions onSave={onSave} onCancel={onCancel} saving={saving} />
        </div>
      </div>
    </div>
  );
}

export function StammdatenPage() {
  const { user } = useAuth();
  // Rolle Verwaltung: nur Mitarbeiter-Tab, nur Kontaktdaten (Server erzwingt das ebenfalls).
  const fullAccess = isGfRole(user?.roleName, INSTANCE, user?.roleScope);
  const visibleTabs = fullAccess ? tabs : tabs.filter(t => t.key === "mitarbeiter");
  const [tab, setTab] = useState<Tab>("mitarbeiter");
  const [search, setSearch] = useState("");

  return (
    <div className="animate-in">
      <div className="mb-10">
        <Eyebrow>Daten · Stammdaten</Eyebrow>
        <h1
          className="text-2xl font-bold mt-4"
          style={{ fontFamily: "var(--font-display)", color: "var(--color-foreground)" }}
        >
          Stammdaten
        </h1>
        <p
          className="text-xs mt-2"
          style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", textTransform: "uppercase" }}
        >
          {fullAccess ? "Mitarbeiter, Externe, Kunden und Projekte verwalten" : "Kontaktdaten der Mitarbeiter pflegen"}
        </p>
      </div>

      <div
        className="flex items-center gap-4 md:gap-6 mb-6 border-b pb-0 overflow-x-auto"
        style={{ borderColor: "var(--color-border)" }}
      >
        {visibleTabs.map(t => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className="flex items-center gap-2 pb-3 text-sm transition-all cursor-pointer relative"
              style={{
                color: active ? "var(--color-primary)" : "var(--color-muted)",
                fontWeight: active ? 600 : 400,
                fontFamily: "var(--font-display)",
                borderBottom: active ? "2px solid var(--color-primary)" : "2px solid transparent",
                marginBottom: "-1px",
              }}
            >
              <t.icon size={15} strokeWidth={1.5} />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="relative w-72 mb-5">
        <Search size={15} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--color-muted)" }} />
        <input className="input pl-9" placeholder="Suchen..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {tab === "mitarbeiter" && <MitarbeiterTab search={search} fullAccess={fullAccess} />}
      {fullAccess && tab === "externe" && <ExterneTab search={search} />}
      {fullAccess && tab === "personen" && <PersonenTab search={search} />}
      {fullAccess && tab === "kunden" && <KundenTab search={search} />}
      {fullAccess && tab === "projekte" && <ProjekteTab search={search} />}
    </div>
  );
}

const rolleBadge = (rolle: string) => {
  const colors: Record<string, { bg: string; color: string }> = {
    GF: { bg: "rgba(0,45,114,0.1)", color: "var(--color-brand-navy)" },
    PM: { bg: "rgba(0,163,224,0.1)", color: "var(--color-primary)" },
    IT: { bg: "var(--color-info-bg)", color: "var(--color-info)" },
    DSG: { bg: "var(--color-warning-bg)", color: "var(--color-warning)" },
  };
  const s = colors[rolle] ?? { bg: "rgba(107,127,146,0.1)", color: "var(--color-muted)" };
  return <span className="badge" style={{ background: s.bg, color: s.color }}>{rolle}</span>;
};

const mandantBadge = (m: string) => {
  const s = m === "combsol"
    ? { bg: "rgba(0,163,224,0.1)", color: "var(--color-primary)" }
    : { bg: "rgba(0,45,114,0.1)", color: "var(--color-brand-navy)" };
  return <span className="badge" style={{ background: s.bg, color: s.color }}>{m}</span>;
};

const typBadge = (typ: string) => {
  const styles: Record<string, { bg: string; color: string; label: string }> = {
    intern: { bg: "rgba(0,163,224,0.1)", color: "var(--color-primary)", label: "Intern" },
    extern: { bg: "rgba(0,45,114,0.1)", color: "var(--color-brand-navy)", label: "Extern" },
    kapitalgesellschaft: { bg: "var(--color-success-bg)", color: "var(--color-success)", label: "Kapitalges." },
    freelancer: { bg: "var(--color-warning-bg)", color: "var(--color-warning)", label: "Freelancer" },
    einzelunternehmen: { bg: "var(--color-info-bg)", color: "var(--color-info)", label: "Einzeluntern." },
  };
  const s = styles[typ] ?? styles.extern;
  return <span className="badge" style={{ background: s.bg, color: s.color }}>{s.label}</span>;
};

const statusBadge = (status: string) => (
  <span className="badge" style={{
    background: status === "aktiv" ? "var(--color-success-bg)" : "var(--color-danger-bg)",
    color: status === "aktiv" ? "var(--color-success)" : "var(--color-danger)",
  }}>{status}</span>
);

function toDateInput(v: any): string {
  if (!v) return "";
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === "string" && v.length >= 10) return v.slice(0, 10);
  return "";
}

type SortKey = "mandant_id" | "personalnummer" | "nachname" | "email" | "rolle" | "eintrittsdatum" | "austrittsdatum" | "status";

function MitarbeiterTab({ search, fullAccess }: { search: string; fullAccess: boolean }) {
  const [showInactive, setShowInactive] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>("nachname");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const isMobile = useIsMobile();

  const query = trpc.stammdaten.mitarbeiter.list.useQuery({
    search: search || undefined, limit: 200, offset: 0, showInactive,
  });
  const [toast, setToast] = useState<string | null>(null);
  const updateMutation = trpc.stammdaten.mitarbeiter.update.useMutation({
    onSuccess: () => { query.refetch(); },
    onError: (err) => { setToast(`Fehler: ${err.message}`); query.refetch(); },
  });
  // Verwaltung: nur Kontaktfelder, eigener Endpunkt mit striktem Schema.
  const kontaktMutation = trpc.stammdaten.mitarbeiter.updateKontakt.useMutation({
    onSuccess: () => { query.refetch(); },
    onError: (err) => { setToast(`Fehler: ${err.message}`); query.refetch(); },
  });
  const saving = updateMutation.isPending || kontaktMutation.isPending;

  const edit = useRowEdit<any>({
    getSnapshot: (m) => ({
      vorname: m.vorname ?? "",
      nachname: m.nachname ?? "",
      email: m.email ?? "",
      telefon: m.telefon ?? "",
      rolle: m.rolle ?? "",
      eintrittsdatum: toDateInput(m.eintrittsdatum),
      austrittsdatum: toDateInput(m.austrittsdatum),
      status: m.status ?? "aktiv",
    }),
    onSave: async (id, data) => {
      if (!fullAccess) {
        await kontaktMutation.mutateAsync({
          id,
          vorname: data.vorname || undefined,
          nachname: data.nachname || undefined,
          email: data.email || undefined,
          telefon: data.telefon || undefined,
        });
        return;
      }
      await updateMutation.mutateAsync({
        id,
        vorname: data.vorname || undefined,
        nachname: data.nachname || undefined,
        email: data.email || undefined,
        rolle: data.rolle || undefined,
        eintrittsdatum: data.eintrittsdatum || undefined,
        // austrittsdatum als "" senden, damit Backend es auf NULL setzt (Mitarbeiter reaktivieren).
        austrittsdatum: data.austrittsdatum,
        status: data.status || undefined,
      });
    },
  });

  const sorted = useMemo(() => {
    const data = [...(query.data?.data ?? [])];
    data.sort((a: any, b: any) => {
      let va = a[sortKey] ?? "";
      let vb = b[sortKey] ?? "";
      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return data;
  }, [query.data?.data, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortHeader = ({ label, field }: { label: string; field: SortKey }) => (
    <th onClick={() => toggleSort(field)} className="cursor-pointer select-none">
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown
          size={11}
          strokeWidth={1.5}
          style={{ color: sortKey === field ? "var(--color-primary)" : "var(--color-muted)", opacity: sortKey === field ? 1 : 0.4 }}
        />
      </span>
    </th>
  );

  const inlineEditActive = !isMobile && !!edit.editId;

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <label
          className="flex items-center gap-2 text-xs cursor-pointer select-none"
          style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}
        >
          <input
            type="checkbox"
            checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)}
            className="w-4 h-4"
            style={{ borderColor: "var(--color-border)", accentColor: "var(--color-primary)" }}
          />
          Inaktive anzeigen
        </label>
        <span className="text-xs" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>
          {query.data?.total ?? 0} Eintraege
        </span>
      </div>
      <div className="card overflow-x-auto">
        <table className="table table-editable">
          <thead>
            <tr>
              <SortHeader label="Status" field="status" />
              <SortHeader label="PNr" field="personalnummer" />
              <SortHeader label="Name" field="nachname" />
              <th>Firma</th>
              <SortHeader label="Email" field="email" />
              <SortHeader label="Eintritt" field="eintrittsdatum" />
              <SortHeader label="Austritt" field="austrittsdatum" />
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((m: any) => {
              const isEditing = inlineEditActive && edit.editId === m.id;
              return (
                <tr
                  key={m.id}
                  className={isEditing ? "is-editing" : undefined}
                  style={m.status !== "aktiv" ? { opacity: 0.6 } : undefined}
                  onClick={() => edit.tryStartEdit(m)}
                  onKeyDown={isEditing ? edit.onKeyDown : undefined}
                  tabIndex={isEditing ? 0 : undefined}
                >
                  <td>{statusBadge(m.status)}</td>
                  <td style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>{m.personalnummer}</td>
                  <td className="font-medium">
                    {isEditing ? (
                      <div className="flex gap-1" onClick={stop}>
                        <input className="input py-1 px-2 text-xs w-20" value={edit.editData.vorname}
                          onChange={e => edit.setEditData({ ...edit.editData, vorname: e.target.value })} />
                        <input className="input py-1 px-2 text-xs w-24" value={edit.editData.nachname}
                          onChange={e => edit.setEditData({ ...edit.editData, nachname: e.target.value })} />
                      </div>
                    ) : `${m.vorname} ${m.nachname}`}
                  </td>
                  <td style={{ fontSize: '11px', color: 'var(--color-muted)' }}>Combsol</td>
                  <td style={{ color: "var(--color-muted)" }}>
                    {isEditing ? (
                      <input className="input py-1 px-2 text-xs w-40" value={edit.editData.email}
                        onClick={stop}
                        onChange={e => edit.setEditData({ ...edit.editData, email: e.target.value })} />
                    ) : (m.email ?? "—")}
                  </td>
                  <td>
                    {isEditing && fullAccess ? (
                      <input type="date" className="input py-1 px-2 text-xs" value={edit.editData.eintrittsdatum}
                        onClick={stop}
                        onChange={e => edit.setEditData({ ...edit.editData, eintrittsdatum: e.target.value })} />
                    ) : (
                      <span className="text-xs" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>
                        {fmtDate(m.eintrittsdatum)}
                      </span>
                    )}
                  </td>
                  <td>
                    {isEditing && fullAccess ? (
                      <input type="date" className="input py-1 px-2 text-xs" value={edit.editData.austrittsdatum}
                        onClick={stop}
                        onChange={e => edit.setEditData({ ...edit.editData, austrittsdatum: e.target.value })} />
                    ) : (
                      <span className="text-xs" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>
                        {fmtDate(m.austrittsdatum)}
                      </span>
                    )}
                  </td>
                  <td>
                    {isEditing && fullAccess ? (
                      <select className="input py-1 px-2 text-xs w-24" value={edit.editData.status}
                        onClick={stop}
                        onChange={e => edit.setEditData({ ...edit.editData, status: e.target.value })}>
                        <option value="aktiv">aktiv</option>
                        <option value="inaktiv">inaktiv</option>
                        <option value="archiviert">archiviert</option>
                      </select>
                    ) : statusBadge(m.status)}
                  </td>
                  <td>
                    {isEditing
                      ? <EditActions onSave={edit.save} onCancel={edit.cancel} saving={saving} />
                      : null}
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={9} className="text-center py-8" style={{ color: "var(--color-muted)" }}>Keine Mitarbeiter gefunden.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <MobileEditModal
        open={isMobile && !!edit.editId}
        title="Mitarbeiter bearbeiten"
        onSave={edit.save}
        onCancel={edit.cancel}
        onKeyDown={edit.onKeyDown}
        saving={saving}
      >
        <MobileField label="Vorname"><input className="input" value={edit.editData.vorname ?? ""} onChange={e => edit.setEditData({ ...edit.editData, vorname: e.target.value })} /></MobileField>
        <MobileField label="Nachname"><input className="input" value={edit.editData.nachname ?? ""} onChange={e => edit.setEditData({ ...edit.editData, nachname: e.target.value })} /></MobileField>
        <MobileField label="Email"><input className="input" value={edit.editData.email ?? ""} onChange={e => edit.setEditData({ ...edit.editData, email: e.target.value })} /></MobileField>
        <MobileField label="Telefon"><input className="input" value={edit.editData.telefon ?? ""} onChange={e => edit.setEditData({ ...edit.editData, telefon: e.target.value })} /></MobileField>
        {fullAccess && (<>
        <MobileField label="Rolle">
          <select className="input" value={edit.editData.rolle ?? ""} onChange={e => edit.setEditData({ ...edit.editData, rolle: e.target.value })}>
            {["GF", "PM", "OPM", "OPS", "IT", "DSG"].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </MobileField>
        <MobileField label="Eintritt"><input type="date" className="input" value={edit.editData.eintrittsdatum ?? ""} onChange={e => edit.setEditData({ ...edit.editData, eintrittsdatum: e.target.value })} /></MobileField>
        <MobileField label="Austritt"><input type="date" className="input" value={edit.editData.austrittsdatum ?? ""} onChange={e => edit.setEditData({ ...edit.editData, austrittsdatum: e.target.value })} /></MobileField>
        <MobileField label="Status">
          <select className="input" value={edit.editData.status ?? "aktiv"} onChange={e => edit.setEditData({ ...edit.editData, status: e.target.value })}>
            <option value="aktiv">aktiv</option><option value="inaktiv">inaktiv</option><option value="archiviert">archiviert</option>
          </select>
        </MobileField>
        </>)}
      </MobileEditModal>
      {toast && <ToastBanner message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}

function MobileField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wider" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>{label}</span>
      {children}
    </label>
  );
}

function ToastBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div
      role="status"
      style={{
        position: "fixed", bottom: 20, right: 20, zIndex: 60,
        background: "var(--color-danger-bg)", color: "var(--color-danger)",
        border: "1px solid var(--color-danger)", padding: "10px 14px",
        borderRadius: 6, fontFamily: "var(--font-mono)", fontSize: 12,
      }}
    >
      {message}
    </div>
  );
}

type ExtSortKey = "nachname" | "firma" | "typ" | "email" | "status" | "eintrittsdatum" | "austrittsdatum";

function ExterneTab({ search }: { search: string }) {
  const [showInactive, setShowInactive] = useState(false);
  const [sortKey, setSortKey] = useState<ExtSortKey>("nachname");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const isMobile = useIsMobile();
  const [toast, setToast] = useState<string | null>(null);

  const query = trpc.stammdaten.externe.list.useQuery({ search: search || undefined, limit: 200, offset: 0, showInactive });
  const completenessQuery = trpc.stammdaten.personen.completeness.useQuery(undefined, { staleTime: 60000 });
  const completenessMap = useMemo(() => {
    const m = new Map<string, any>();
    for (const c of completenessQuery.data?.persons ?? []) {
      if (c.email) m.set(c.email.toLowerCase(), c);
      m.set(`${c.vorname} ${c.nachname}`.toLowerCase(), c);
    }
    return m;
  }, [completenessQuery.data]);
  const updateMutation = trpc.stammdaten.externe.update.useMutation({
    onSuccess: () => { query.refetch(); },
    onError: (err) => { setToast(`Fehler: ${err.message}`); query.refetch(); },
  });

  const edit = useRowEdit<any>({
    getSnapshot: (r) => ({
      vorname: r.vorname ?? "", nachname: r.nachname ?? "",
      firma: r.firma ?? "", email: r.email ?? "",
      typ: r.typ ?? "", status: r.status ?? "aktiv",
    }),
    onSave: async (id, data) => {
      await updateMutation.mutateAsync({ id, ...data });
    },
  });

  const sorted = useMemo(() => {
    const data = [...(query.data?.data ?? [])];
    data.sort((a: any, b: any) => {
      let va = a[sortKey] ?? ""; let vb = b[sortKey] ?? "";
      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return data;
  }, [query.data?.data, sortKey, sortDir]);

  const toggleSort = (key: ExtSortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortTh = ({ label, field }: { label: string; field: ExtSortKey }) => (
    <th onClick={() => toggleSort(field)} className="cursor-pointer select-none">
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown
          size={11}
          strokeWidth={1.5}
          style={{ color: sortKey === field ? "var(--color-primary)" : "var(--color-muted)", opacity: sortKey === field ? 1 : 0.4 }}
        />
      </span>
    </th>
  );

  const inlineEditActive = !isMobile && !!edit.editId;

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <label
          className="flex items-center gap-2 text-xs cursor-pointer select-none"
          style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}
        >
          <input
            type="checkbox"
            checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)}
            className="w-4 h-4"
            style={{ borderColor: "var(--color-border)", accentColor: "var(--color-primary)" }}
          />
          Inaktive anzeigen
        </label>
        <span className="text-xs" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>
          {query.data?.total ?? 0} Eintraege
        </span>
      </div>
      <div className="card overflow-x-auto">
        <table className="table table-editable">
          <thead><tr>
            <SortTh label="Name" field="nachname" />
            <SortTh label="Firma" field="firma" />
            <SortTh label="Email" field="email" />
            <SortTh label="Eintritt" field="eintrittsdatum" />
            <SortTh label="Austritt" field="austrittsdatum" />
            <SortTh label="Status" field="status" />
            <th></th>
          </tr></thead>
          <tbody>
            {sorted.map((e: any) => {
              const isEditing = inlineEditActive && edit.editId === e.id;
              return (
                <tr
                  key={e.id}
                  className={isEditing ? "is-editing" : undefined}
                  style={e.status !== "aktiv" ? { opacity: 0.6 } : undefined}
                  onClick={() => edit.tryStartEdit(e)}
                  onKeyDown={isEditing ? edit.onKeyDown : undefined}
                  tabIndex={isEditing ? 0 : undefined}
                >
                  <td className="font-medium">
                    {isEditing ? (
                      <div className="flex gap-1" onClick={stop}>
                        <input className="input py-1 px-2 text-xs w-20" value={edit.editData.vorname} onChange={ev => edit.setEditData({ ...edit.editData, vorname: ev.target.value })} />
                        <input className="input py-1 px-2 text-xs w-24" value={edit.editData.nachname} onChange={ev => edit.setEditData({ ...edit.editData, nachname: ev.target.value })} />
                      </div>
                    ) : `${e.vorname} ${e.nachname}`}
                  </td>
                  <td>{isEditing ? <input className="input py-1 px-2 text-xs w-36" value={edit.editData.firma} onClick={stop} onChange={ev => edit.setEditData({ ...edit.editData, firma: ev.target.value })} /> : (e.firma ?? "—")}</td>

                  <td style={{ color: "var(--color-muted)" }}>{isEditing ? <input className="input py-1 px-2 text-xs w-40" value={edit.editData.email} onClick={stop} onChange={ev => edit.setEditData({ ...edit.editData, email: ev.target.value })} /> : (e.email ?? "—")}</td>
                  {(() => {
                    const c = completenessMap.get((e.email ?? "").toLowerCase()) || completenessMap.get(`${e.vorname} ${e.nachname}`.toLowerCase());
                    const eintritt = c?.lnv?.eintritt;
                    const austritt = c?.lnv?.austritt;
                    return <>
                      <td><span className="text-xs" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>{fmtDate(eintritt)}</span></td>
                      <td><span className="text-xs" style={{ color: austritt ? "var(--color-danger)" : "var(--color-muted)", fontFamily: "var(--font-mono)" }}>{fmtDate(austritt)}</span></td>
                    </>;
                  })()}
                  <td>{isEditing ? (
                    <select className="input py-1 px-2 text-xs w-24" value={edit.editData.status} onClick={stop} onChange={ev => edit.setEditData({ ...edit.editData, status: ev.target.value })}>
                      <option value="aktiv">aktiv</option><option value="inaktiv">inaktiv</option><option value="archiviert">archiviert</option>
                    </select>
                  ) : statusBadge(e.status)}</td>
                  <td>{isEditing ? <EditActions onSave={edit.save} onCancel={edit.cancel} saving={updateMutation.isPending} /> : null}</td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8" style={{ color: "var(--color-muted)" }}>Keine Externen gefunden.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <MobileEditModal
        open={isMobile && !!edit.editId}
        title="Externe bearbeiten"
        onSave={edit.save}
        onCancel={edit.cancel}
        onKeyDown={edit.onKeyDown}
        saving={updateMutation.isPending}
      >
        <MobileField label="Vorname"><input className="input" value={edit.editData.vorname ?? ""} onChange={e => edit.setEditData({ ...edit.editData, vorname: e.target.value })} /></MobileField>
        <MobileField label="Nachname"><input className="input" value={edit.editData.nachname ?? ""} onChange={e => edit.setEditData({ ...edit.editData, nachname: e.target.value })} /></MobileField>
        <MobileField label="Firma"><input className="input" value={edit.editData.firma ?? ""} onChange={e => edit.setEditData({ ...edit.editData, firma: e.target.value })} /></MobileField>
        <MobileField label="Email"><input className="input" value={edit.editData.email ?? ""} onChange={e => edit.setEditData({ ...edit.editData, email: e.target.value })} /></MobileField>
        <MobileField label="Typ">
          <select className="input" value={edit.editData.typ ?? ""} onChange={e => edit.setEditData({ ...edit.editData, typ: e.target.value })}>
            <option value="kapitalgesellschaft">Kapitalges.</option>
            <option value="freelancer">Freelancer</option>
            <option value="einzelunternehmen">Einzeluntern.</option>
          </select>
        </MobileField>
        <MobileField label="Status">
          <select className="input" value={edit.editData.status ?? "aktiv"} onChange={e => edit.setEditData({ ...edit.editData, status: e.target.value })}>
            <option value="aktiv">aktiv</option><option value="inaktiv">inaktiv</option><option value="archiviert">archiviert</option>
          </select>
        </MobileField>
      </MobileEditModal>
      {toast && <ToastBanner message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}

type PersSortKey = "mandant_id" | "typ" | "nachname" | "email" | "status" | "eintrittsdatum" | "austrittsdatum";

function CellYesNo({ value, na }: { value: boolean | null | undefined; na?: boolean }) {
  if (na) return <span style={{ color: "var(--color-muted)", fontSize: "11px" }}>n/a</span>;
  if (value === null || value === undefined) return <span style={{ color: "var(--color-muted)" }}>—</span>;
  return value
    ? <span style={{ color: "#22c55e", fontWeight: 600, fontSize: "11px" }}>Ja</span>
    : <span style={{ color: "#ef4444", fontWeight: 600, fontSize: "11px" }}>Nein</span>;
}

function PersonenTab({ search }: { search: string }) {
  const [showInactive, setShowInactive] = useState(false);
  const [sortKey, setSortKey] = useState<PersSortKey>("nachname");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const isMobile = useIsMobile();
  const [toast, setToast] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newPersonKind, setNewPersonKind] = useState<"extern" | "intern">("extern");
  const emptyPerson = {
    vorname: "",
    nachname: "",
    firma: "",
    typ: "kapitalgesellschaft" as "kapitalgesellschaft" | "freelancer" | "einzelunternehmen",
    email: "",
    mandantId: "combsol" as "combsol" | "coorconn",
    personalnummer: "",
    eintrittsdatum: "",
    rolle: "OPS" as "GF" | "PM" | "OPM" | "OPS" | "IT" | "DSG",
  };
  const [newPerson, setNewPerson] = useState(emptyPerson);

  const query = trpc.stammdaten.personen.list.useQuery({ search: search || undefined, limit: 200, offset: 0, showInactive });
  const completenessQuery = trpc.stammdaten.personen.completeness.useQuery(undefined, { staleTime: 60_000 });
  const completenessMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const p of completenessQuery.data?.persons ?? []) {
      const key = `${p.vorname} ${p.nachname}`.toLowerCase();
      map.set(key, p);
      if (p.email) map.set(p.email.toLowerCase(), p);
    }
    return map;
  }, [completenessQuery.data]);
  const syncMessage = (prefix: string, sync: { succeeded: number; applicable: number } | null | undefined) => {
    if (!sync) return `${prefix}. Kein Benutzerkonto angelegt.`;
    return `${prefix}. Kein Benutzerkonto angelegt. Synchronisation: ${sync.succeeded}/${sync.applicable} Zielsysteme bestätigt.`;
  };
  const afterCreate = (sync: { succeeded: number; applicable: number } | null | undefined) => {
    query.refetch();
    completenessQuery.refetch();
    setShowCreate(false);
    setNewPerson(emptyPerson);
    setToast(syncMessage("Personenstammdatensatz angelegt", sync));
  };
  const updateMutation = trpc.stammdaten.personen.update.useMutation({
    onSuccess: (result) => {
      query.refetch();
      completenessQuery.refetch();
      setToast(result.sync
        ? `Stammdaten gespeichert. Synchronisation: ${result.sync.succeeded}/${result.sync.applicable} Zielsysteme bestätigt.`
        : "Stammdaten gespeichert.");
    },
    onError: (err) => { setToast(`Fehler: ${err.message}`); query.refetch(); },
  });
  const externalCreateMutation = trpc.stammdaten.externe.create.useMutation({
    onSuccess: (result) => afterCreate(result.sync),
    onError: (err) => { setToast(`Fehler: ${err.message}`); },
  });
  const internalCreateMutation = trpc.stammdaten.mitarbeiter.create.useMutation({
    onSuccess: (result) => afterCreate(result.sync),
    onError: (err) => { setToast(`Fehler: ${err.message}`); },
  });

  const edit = useRowEdit<any>({
    getSnapshot: (p) => ({
      typ: p.typ, // read-only — braucht Backend fuer Routing
      vorname: p.vorname ?? "", nachname: p.nachname ?? "",
      firma: p.firma ?? "", email: p.email ?? "", status: p.status ?? "aktiv",
    }),
    onSave: async (id, data) => {
      await updateMutation.mutateAsync({
        id,
        typ: data.typ,
        vorname: data.vorname || undefined,
        nachname: data.nachname || undefined,
        firma: data.typ === "intern" ? undefined : data.firma || undefined,
        email: data.email || undefined,
        status: data.status || undefined,
      });
    },
  });

  const sorted = useMemo(() => {
    const data = [...(query.data?.data ?? [])];
    data.sort((a: any, b: any) => {
      let va = a[sortKey] ?? ""; let vb = b[sortKey] ?? "";
      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return data;
  }, [query.data?.data, sortKey, sortDir]);

  const toggleSort = (key: PersSortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortTh = ({ label, field }: { label: string; field: PersSortKey }) => (
    <th onClick={() => toggleSort(field)} className="cursor-pointer select-none">
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown
          size={11}
          strokeWidth={1.5}
          style={{ color: sortKey === field ? "var(--color-primary)" : "var(--color-muted)", opacity: sortKey === field ? 1 : 0.4 }}
        />
      </span>
    </th>
  );

  const inlineEditActive = !isMobile && !!edit.editId;
  const createPending = externalCreateMutation.isPending || internalCreateMutation.isPending;
  const createDisabled = createPending || !newPerson.vorname || !newPerson.nachname || (
    newPersonKind === "extern"
      ? !newPerson.firma
      : !newPerson.personalnummer || !newPerson.email || !newPerson.eintrittsdatum
  );
  const submitCreate = () => {
    if (newPersonKind === "extern") {
      externalCreateMutation.mutate({
        vorname: newPerson.vorname,
        nachname: newPerson.nachname,
        firma: newPerson.firma,
        typ: newPerson.typ,
        email: newPerson.email || undefined,
        mandantId: newPerson.mandantId,
      });
      return;
    }
    internalCreateMutation.mutate({
      mandant_id: newPerson.mandantId,
      personalnummer: newPerson.personalnummer,
      vorname: newPerson.vorname,
      nachname: newPerson.nachname,
      email: newPerson.email,
      eintrittsdatum: newPerson.eintrittsdatum,
      rolle: newPerson.rolle,
    });
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => setShowCreate(true)}
          style={{ background: "var(--color-primary)", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", padding: "6px 12px", fontSize: "12px", fontWeight: 600 }}
        >
          + Person anlegen
        </button>
        <label
          className="flex items-center gap-2 text-xs cursor-pointer select-none"
          style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}
        >
          <input
            type="checkbox"
            checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)}
            className="w-4 h-4"
            style={{ borderColor: "var(--color-border)", accentColor: "var(--color-primary)" }}
          />
          Inaktive anzeigen
        </label>
        <span className="text-xs" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>
          {query.data?.total ?? 0} Personen
        </span>
      </div>
      {showCreate && (
        <div className="card mb-4" style={{ padding: 16, border: "1px solid var(--color-primary)" }}>
          <h4 style={{ margin: "0 0 8px", fontSize: 14 }}>Personenstammdatensatz anlegen</h4>
          <p style={{ fontSize: 11, color: "var(--color-muted)", margin: "0 0 12px" }}>
            Der Datensatz wird an die fachlichen Apps synchronisiert. <strong>Es wird kein Benutzerkonto und kein App-Zugang angelegt.</strong>
          </p>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            {(["extern", "intern"] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                onClick={() => setNewPersonKind(kind)}
                style={{
                  background: newPersonKind === kind ? "var(--color-primary)" : "transparent",
                  color: newPersonKind === kind ? "#fff" : "var(--color-text)",
                  border: "1px solid var(--color-primary)", borderRadius: 4,
                  cursor: "pointer", padding: "6px 12px", fontSize: 12, fontWeight: 600,
                }}
              >{kind === "extern" ? "Externe Person" : "Interne Person"}</button>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
            <div><label style={{fontSize:10,color:"var(--color-muted)"}}>Vorname *</label><input className="input" style={{width:"100%"}} value={newPerson.vorname} onChange={e => setNewPerson({...newPerson, vorname: e.target.value})} /></div>
            <div><label style={{fontSize:10,color:"var(--color-muted)"}}>Nachname *</label><input className="input" style={{width:"100%"}} value={newPerson.nachname} onChange={e => setNewPerson({...newPerson, nachname: e.target.value})} /></div>
            {newPersonKind === "extern" ? <>
              <div><label style={{fontSize:10,color:"var(--color-muted)"}}>Firma *</label><input className="input" style={{width:"100%"}} value={newPerson.firma} onChange={e => setNewPerson({...newPerson, firma: e.target.value})} /></div>
              <div><label style={{fontSize:10,color:"var(--color-muted)"}}>Art</label><select className="input" style={{width:"100%"}} value={newPerson.typ} onChange={e => setNewPerson({...newPerson, typ: e.target.value as typeof newPerson.typ})}><option value="kapitalgesellschaft">Kapitalgesellschaft</option><option value="freelancer">Freelancer</option><option value="einzelunternehmen">Einzelunternehmen</option></select></div>
              <div><label style={{fontSize:10,color:"var(--color-muted)"}}>E-Mail</label><input type="email" className="input" style={{width:"100%"}} value={newPerson.email} onChange={e => setNewPerson({...newPerson, email: e.target.value})} /></div>
            </> : <>
              <div><label style={{fontSize:10,color:"var(--color-muted)"}}>Mandant *</label><select className="input" style={{width:"100%"}} value={newPerson.mandantId} onChange={e => setNewPerson({...newPerson, mandantId: e.target.value as typeof newPerson.mandantId})}><option value="combsol">Combsol GmbH</option><option value="coorconn">CoorConn GmbH</option></select></div>
              <div><label style={{fontSize:10,color:"var(--color-muted)"}}>Personalnummer *</label><input className="input" style={{width:"100%"}} value={newPerson.personalnummer} onChange={e => setNewPerson({...newPerson, personalnummer: e.target.value})} /></div>
              <div><label style={{fontSize:10,color:"var(--color-muted)"}}>E-Mail *</label><input type="email" className="input" style={{width:"100%"}} value={newPerson.email} onChange={e => setNewPerson({...newPerson, email: e.target.value})} /></div>
              <div><label style={{fontSize:10,color:"var(--color-muted)"}}>Eintritt *</label><input type="date" className="input" style={{width:"100%"}} value={newPerson.eintrittsdatum} onChange={e => setNewPerson({...newPerson, eintrittsdatum: e.target.value})} /></div>
              <div><label style={{fontSize:10,color:"var(--color-muted)"}}>Organisationsrolle *</label><select className="input" style={{width:"100%"}} value={newPerson.rolle} onChange={e => setNewPerson({...newPerson, rolle: e.target.value as typeof newPerson.rolle})}><option value="OPS">OPS</option><option value="OPM">OPM</option><option value="PM">PM</option><option value="IT">IT</option><option value="DSG">DSG</option><option value="GF">GF</option></select></div>
            </>}
          </div>
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <button
              onClick={submitCreate}
              disabled={createDisabled}
              style={{ background: "var(--color-primary)", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", padding: "6px 16px", fontSize: 12, opacity: createDisabled ? 0.5 : 1 }}
            >{createPending ? "Wird angelegt und synchronisiert..." : "Stammdatensatz anlegen"}</button>
            <button onClick={() => setShowCreate(false)} style={{ background: "transparent", color: "var(--color-muted)", border: "1px solid var(--color-border)", borderRadius: 4, cursor: "pointer", padding: "6px 16px", fontSize: 12 }}>Abbrechen</button>
          </div>
        </div>
      )}
      <div className="card overflow-x-auto">
        <table className="table table-editable">
          <thead><tr>
            <SortTh label="Name" field="nachname" />
            <th>Firma</th>
            <SortTh label="Email" field="email" />
            <SortTh label="Eintritt" field="eintrittsdatum" />
            <SortTh label="Austritt" field="austrittsdatum" />
            <SortTh label="Status" field="status" />
            <th title="Quelle: FAIR-Kapa (nur dort bearbeitbar)" style={{fontSize:'10px',whiteSpace:'nowrap'}}>Einsatzplan<br/><span style={{color:'var(--color-muted)',fontWeight:400}}>FAIR-Kapa</span></th>
            <th title="Quelle: FAIR-Kapa (nur dort bearbeitbar)" style={{fontSize:'10px',whiteSpace:'nowrap'}}>Tagessatz<br/><span style={{color:'var(--color-muted)',fontWeight:400}}>FAIR-Kapa</span></th>
            <th title="Quelle: FAIR-Kapa (nur dort bearbeitbar)" style={{fontSize:'10px',whiteSpace:'nowrap'}}>LV-Position<br/><span style={{color:'var(--color-muted)',fontWeight:400}}>FAIR-Kapa</span></th>
            <th title="Quelle: Vertrags-App (nur dort bearbeitbar)" style={{fontSize:'10px',whiteSpace:'nowrap'}}>Honorarblatt<br/><span style={{color:'var(--color-muted)',fontWeight:400}}>Vertrag</span></th>
            <th></th>
          </tr></thead>
          <tbody>
            {sorted.map((p: any, i: number) => {
              const isEditing = inlineEditActive && edit.editId === p.id;
              return (
                <tr
                  key={`${p.id}-${i}`}
                  className={isEditing ? "is-editing" : undefined}
                  style={p.status !== "aktiv" ? { opacity: 0.6 } : undefined}
                  onClick={() => edit.tryStartEdit(p)}
                  onKeyDown={isEditing ? edit.onKeyDown : undefined}
                  tabIndex={isEditing ? 0 : undefined}
                >
                  <td className="font-medium">
                    {isEditing ? (
                      <div className="flex gap-1" onClick={stop}>
                        <input className="input py-1 px-2 text-xs w-20" value={edit.editData.vorname} onChange={e => edit.setEditData({ ...edit.editData, vorname: e.target.value })} />
                        <input className="input py-1 px-2 text-xs w-24" value={edit.editData.nachname} onChange={e => edit.setEditData({ ...edit.editData, nachname: e.target.value })} />
                      </div>
                    ) : `${p.vorname} ${p.nachname}`}
                  </td>
                  <td style={{ fontSize: '11px', color: 'var(--color-muted)' }}>
                    {isEditing && p.typ !== "intern"
                      ? <input className="input py-1 px-2 text-xs w-40" value={edit.editData.firma} onClick={stop} onChange={e => edit.setEditData({ ...edit.editData, firma: e.target.value })} />
                      : (p.firma ?? '—')}
                  </td>
                  <td style={{ color: "var(--color-muted)" }}>
                    {isEditing ? <input className="input py-1 px-2 text-xs w-40" value={edit.editData.email} onClick={stop} onChange={e => edit.setEditData({ ...edit.editData, email: e.target.value })} /> : (p.email ?? "—")}
                  </td>
                  {(() => {
                    const c = completenessMap.get((p.email ?? "").toLowerCase()) || completenessMap.get(`${p.vorname} ${p.nachname}`.toLowerCase());
                    const eintritt = p.eintrittsdatum || c?.lnv?.eintritt;
                    const austritt = p.austrittsdatum || c?.lnv?.austritt;
                    return <>
                      <td><span className="text-xs" style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>{fmtDate(eintritt)}</span></td>
                      <td><span className="text-xs" style={{ color: austritt ? "var(--color-danger)" : "var(--color-muted)", fontFamily: "var(--font-mono)" }}>{fmtDate(austritt)}</span></td>
                    </>;
                  })()}
                  <td>
                    {isEditing ? (
                      <select className="input py-1 px-2 text-xs w-24" value={edit.editData.status} onClick={stop} onChange={e => edit.setEditData({ ...edit.editData, status: e.target.value })}>
                        <option value="aktiv">aktiv</option><option value="inaktiv">inaktiv</option><option value="archiviert">archiviert</option>
                      </select>
                    ) : statusBadge(p.status)}
                  </td>
                  {(() => {
                    const c = completenessMap.get((p.email ?? "").toLowerCase()) || completenessMap.get(`${p.vorname} ${p.nachname}`.toLowerCase());
                    const isIntern = p.typ === "intern";
                    return <>
                      <td><CellYesNo value={c?.fairKapa?.hatEinsatzplan} /></td>
                      <td><CellYesNo value={c?.fairKapa?.hatTagessatz} /></td>
                      <td><CellYesNo value={c?.fairKapa?.hatLvPosition} /></td>
                      <td><CellYesNo value={c?.vertrag?.hatHonorarblatt} /></td>
                    </>;
                  })()}
                  <td>
                    {isEditing ? <EditActions onSave={edit.save} onCancel={edit.cancel} saving={updateMutation.isPending} /> : null}
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr><td colSpan={10} className="text-center py-8" style={{ color: "var(--color-muted)" }}>Keine Personen gefunden.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <MobileEditModal
        open={isMobile && !!edit.editId}
        title="Person bearbeiten"
        onSave={edit.save}
        onCancel={edit.cancel}
        onKeyDown={edit.onKeyDown}
        saving={updateMutation.isPending}
      >
        <MobileField label="Vorname"><input className="input" value={edit.editData.vorname ?? ""} onChange={e => edit.setEditData({ ...edit.editData, vorname: e.target.value })} /></MobileField>
        <MobileField label="Nachname"><input className="input" value={edit.editData.nachname ?? ""} onChange={e => edit.setEditData({ ...edit.editData, nachname: e.target.value })} /></MobileField>
        {edit.editData.typ !== "intern" && <MobileField label="Firma"><input className="input" value={edit.editData.firma ?? ""} onChange={e => edit.setEditData({ ...edit.editData, firma: e.target.value })} /></MobileField>}
        <MobileField label="Email"><input className="input" value={edit.editData.email ?? ""} onChange={e => edit.setEditData({ ...edit.editData, email: e.target.value })} /></MobileField>
        <MobileField label="Status">
          <select className="input" value={edit.editData.status ?? "aktiv"} onChange={e => edit.setEditData({ ...edit.editData, status: e.target.value })}>
            <option value="aktiv">aktiv</option><option value="inaktiv">inaktiv</option><option value="archiviert">archiviert</option>
          </select>
        </MobileField>
      </MobileEditModal>
      {toast && <ToastBanner message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}

function KundenTab({ search }: { search: string }) {
  const query = trpc.stammdaten.kunden.list.useQuery({ search: search || undefined, limit: 50, offset: 0 });
  const isMobile = useIsMobile();
  const [toast, setToast] = useState<string | null>(null);
  const updateMutation = trpc.stammdaten.kunden.update.useMutation({
    onSuccess: () => { query.refetch(); },
    onError: (err) => { setToast(`Fehler: ${err.message}`); query.refetch(); },
  });

  const edit = useRowEdit<any>({
    getSnapshot: (k) => ({
      firma: k.firma ?? "", branche: k.branche ?? "",
      kontaktperson: k.kontaktperson ?? "", email: k.email ?? "",
      telefon: k.telefon ?? "",
    }),
    onSave: async (uuid, data) => {
      await updateMutation.mutateAsync({ uuid, ...data });
    },
  });

  const inlineEditActive = !isMobile && !!edit.editId;

  return (
    <div>
      <div className="card overflow-x-auto">
        <div
          className="px-4 py-3 border-b text-xs"
          style={{ borderColor: "var(--color-border)", color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}
        >
          {query.data?.total ?? 0} Kunden
        </div>
        <table className="table table-editable">
          <thead><tr><th>Firma</th><th>Branche</th><th>Kontakt</th><th>Email</th><th>Telefon</th><th></th></tr></thead>
          <tbody>
            {query.data?.data.map((k: any) => {
              const key = k.uuid || String(k.id);
              const isEditing = inlineEditActive && edit.editId === key;
              return (
                <tr
                  key={key}
                  className={isEditing ? "is-editing" : undefined}
                  onClick={() => edit.tryStartEdit({ ...k, id: key })}
                  onKeyDown={isEditing ? edit.onKeyDown : undefined}
                  tabIndex={isEditing ? 0 : undefined}
                >
                  <td className="font-medium">
                    {isEditing
                      ? <input className="input py-1 px-2 text-xs w-44" value={edit.editData.firma} onClick={stop} onChange={e => edit.setEditData({ ...edit.editData, firma: e.target.value })} />
                      : (k.firma ?? k.name)}
                  </td>
                  <td>
                    {isEditing
                      ? <input className="input py-1 px-2 text-xs w-32" value={edit.editData.branche} onClick={stop} onChange={e => edit.setEditData({ ...edit.editData, branche: e.target.value })} />
                      : (k.branche ?? "—")}
                  </td>
                  <td>
                    {isEditing
                      ? <input className="input py-1 px-2 text-xs w-40" value={edit.editData.kontaktperson} onClick={stop} onChange={e => edit.setEditData({ ...edit.editData, kontaktperson: e.target.value })} />
                      : (k.kontaktperson ?? k.ansprechpartner ?? "—")}
                  </td>
                  <td style={{ color: "var(--color-muted)" }}>
                    {isEditing
                      ? <input className="input py-1 px-2 text-xs w-40" value={edit.editData.email} onClick={stop} onChange={e => edit.setEditData({ ...edit.editData, email: e.target.value })} />
                      : (k.email ?? "—")}
                  </td>
                  <td>
                    {isEditing
                      ? <input className="input py-1 px-2 text-xs w-32" value={edit.editData.telefon} onClick={stop} onChange={e => edit.setEditData({ ...edit.editData, telefon: e.target.value })} />
                      : (k.telefon ?? "—")}
                  </td>
                  <td>
                    {isEditing ? <EditActions onSave={edit.save} onCancel={edit.cancel} saving={updateMutation.isPending} /> : null}
                  </td>
                </tr>
              );
            })}
            {query.data?.data.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8" style={{ color: "var(--color-muted)" }}>Keine Kunden vorhanden.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <MobileEditModal
        open={isMobile && !!edit.editId}
        title="Kunde bearbeiten"
        onSave={edit.save}
        onCancel={edit.cancel}
        onKeyDown={edit.onKeyDown}
        saving={updateMutation.isPending}
      >
        <MobileField label="Firma"><input className="input" value={edit.editData.firma ?? ""} onChange={e => edit.setEditData({ ...edit.editData, firma: e.target.value })} /></MobileField>
        <MobileField label="Branche"><input className="input" value={edit.editData.branche ?? ""} onChange={e => edit.setEditData({ ...edit.editData, branche: e.target.value })} /></MobileField>
        <MobileField label="Kontakt"><input className="input" value={edit.editData.kontaktperson ?? ""} onChange={e => edit.setEditData({ ...edit.editData, kontaktperson: e.target.value })} /></MobileField>
        <MobileField label="Email"><input className="input" value={edit.editData.email ?? ""} onChange={e => edit.setEditData({ ...edit.editData, email: e.target.value })} /></MobileField>
        <MobileField label="Telefon"><input className="input" value={edit.editData.telefon ?? ""} onChange={e => edit.setEditData({ ...edit.editData, telefon: e.target.value })} /></MobileField>
      </MobileEditModal>
      {toast && <ToastBanner message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}

function ProjekteTab({ search }: { search: string }) {
  const query = trpc.stammdaten.projekte.list.useQuery({ search: search || undefined, limit: 50, offset: 0 });
  const isMobile = useIsMobile();
  const [toast, setToast] = useState<string | null>(null);
  const updateMutation = trpc.stammdaten.projekte.update.useMutation({
    onSuccess: () => { query.refetch(); },
    onError: (err) => { setToast(`Fehler: ${err.message}`); query.refetch(); },
  });

  const edit = useRowEdit<any>({
    getSnapshot: (p) => ({
      name: p.name ?? "",
      projektnummer: p.projektnummer ?? "",
      status: p.status ?? "aktiv",
      startDatum: toDateInput(p.startDatum ?? p.start_datum),
      endeDatum: toDateInput(p.endeDatum ?? p.end_datum),
    }),
    onSave: async (uuid, data) => {
      await updateMutation.mutateAsync({
        uuid,
        name: data.name || undefined,
        projektnummer: data.projektnummer || undefined,
        status: data.status || undefined,
        startDatum: data.startDatum ?? undefined,
        endeDatum: data.endeDatum ?? undefined,
      });
    },
  });

  const inlineEditActive = !isMobile && !!edit.editId;

  return (
    <div>
      <div className="card overflow-x-auto">
        <div
          className="px-4 py-3 border-b text-xs"
          style={{ borderColor: "var(--color-border)", color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}
        >
          {query.data?.total ?? 0} Projekte
        </div>
        <table className="table table-editable">
          <thead><tr><th>Projekt</th><th>Projektnr.</th><th>Status</th><th>Start</th><th>Ende</th><th></th></tr></thead>
          <tbody>
            {query.data?.data.map((p: any) => {
              const key = p.uuid || String(p.id);
              const isEditing = inlineEditActive && edit.editId === key;
              return (
                <tr
                  key={key}
                  className={isEditing ? "is-editing" : undefined}
                  onClick={() => edit.tryStartEdit({ ...p, id: key })}
                  onKeyDown={isEditing ? edit.onKeyDown : undefined}
                  tabIndex={isEditing ? 0 : undefined}
                >
                  <td className="font-medium">
                    {isEditing
                      ? <input className="input py-1 px-2 text-xs w-44" value={edit.editData.name} onClick={stop} onChange={e => edit.setEditData({ ...edit.editData, name: e.target.value })} />
                      : p.name}
                  </td>
                  <td style={{ fontFamily: "var(--font-mono)" }}>
                    {isEditing
                      ? <input className="input py-1 px-2 text-xs w-32" value={edit.editData.projektnummer} onClick={stop} onChange={e => edit.setEditData({ ...edit.editData, projektnummer: e.target.value })} />
                      : (p.projektnummer ?? "—")}
                  </td>
                  <td>
                    {isEditing
                      ? (
                        <select className="input py-1 px-2 text-xs w-28" value={edit.editData.status} onClick={stop} onChange={e => edit.setEditData({ ...edit.editData, status: e.target.value })}>
                          <option value="aktiv">aktiv</option>
                          <option value="abgeschlossen">abgeschlossen</option>
                          <option value="pausiert">pausiert</option>
                          <option value="archiviert">archiviert</option>
                        </select>
                      )
                      : <span className="badge" style={{ background: "var(--color-success-bg)", color: "var(--color-success)" }}>{p.status}</span>}
                  </td>
                  <td style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>
                    {isEditing
                      ? <input type="date" className="input py-1 px-2 text-xs" value={edit.editData.startDatum} onClick={stop} onChange={e => edit.setEditData({ ...edit.editData, startDatum: e.target.value })} />
                      : fmtDate(p.startDatum ?? p.start_datum)}
                  </td>
                  <td style={{ color: "var(--color-muted)", fontFamily: "var(--font-mono)" }}>
                    {isEditing
                      ? <input type="date" className="input py-1 px-2 text-xs" value={edit.editData.endeDatum} onClick={stop} onChange={e => edit.setEditData({ ...edit.editData, endeDatum: e.target.value })} />
                      : fmtDate(p.endeDatum ?? p.end_datum)}
                  </td>
                  <td>
                    {isEditing ? <EditActions onSave={edit.save} onCancel={edit.cancel} saving={updateMutation.isPending} /> : null}
                  </td>
                </tr>
              );
            })}
            {query.data?.data.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8" style={{ color: "var(--color-muted)" }}>Keine Projekte vorhanden.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <MobileEditModal
        open={isMobile && !!edit.editId}
        title="Projekt bearbeiten"
        onSave={edit.save}
        onCancel={edit.cancel}
        onKeyDown={edit.onKeyDown}
        saving={updateMutation.isPending}
      >
        <MobileField label="Name"><input className="input" value={edit.editData.name ?? ""} onChange={e => edit.setEditData({ ...edit.editData, name: e.target.value })} /></MobileField>
        <MobileField label="Projektnummer"><input className="input" value={edit.editData.projektnummer ?? ""} onChange={e => edit.setEditData({ ...edit.editData, projektnummer: e.target.value })} /></MobileField>
        <MobileField label="Status">
          <select className="input" value={edit.editData.status ?? "aktiv"} onChange={e => edit.setEditData({ ...edit.editData, status: e.target.value })}>
            <option value="aktiv">aktiv</option>
            <option value="abgeschlossen">abgeschlossen</option>
            <option value="pausiert">pausiert</option>
            <option value="archiviert">archiviert</option>
          </select>
        </MobileField>
        <MobileField label="Start"><input type="date" className="input" value={edit.editData.startDatum ?? ""} onChange={e => edit.setEditData({ ...edit.editData, startDatum: e.target.value })} /></MobileField>
        <MobileField label="Ende"><input type="date" className="input" value={edit.editData.endeDatum ?? ""} onChange={e => edit.setEditData({ ...edit.editData, endeDatum: e.target.value })} /></MobileField>
      </MobileEditModal>
      {toast && <ToastBanner message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
